import {
  Component,
  input,
  output,
  signal,
  computed,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { FieldsetModule } from 'primeng/fieldset';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { DropZoneDirective } from 'src/app/presentation/shared/file-uploader/drop-zone.directive';

import {
  BloqueAsistentesComponent,
  Asistente
} from '../bloque-asistentes/bloque-asistentes.component';
import { TIPO_CITA, MAX_TEXTO_ACTA } from 'src/app/common/constants-ejb';
import { sanitizeTextarea } from 'src/app/common/input-utils';
import { ContadorCaracteresDirective } from 'src/app/presentation/shared/contador-caracteres/contador-caracteres.directive';

/** Tipos de acta según dsclasificacion del servicio */
export type TipoActa = 'ASESOR' | 'CAPACITACI' | null;

/** Valor de un anexo para enviar al backend */
export interface AnexoActaFormValue {
  nombre: string;
  contentType: string;
  contenidoBase64: string;
}

/** Valor del formulario de acta */
export interface DatosActaFormValue {
  descripcionServicioPrestado: string;
  compromisosEmpresaAfiliada: string;
  compromisosSura: string;
  relacionDocumentacion: string;
  envioNotificacion: 'S' | 'N';
  tipoActa: TipoActa;
  asistentes: Asistente[];
  anexos: AnexoActaFormValue[];
}

const MAX_ANEXOS = 4;
/** 9 MB en bytes de base64 (~12 MB binario) */
const MAX_BASE64_BYTES = 9 * 1024 * 1024;

const MIME_PERMITIDOS = new Set([
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/csv'
]);

/**
 * DUMB COMPONENT: SeccionActa
 *
 * Panel de asociación de actas para cumplimientos Tipo Campo.
 * Visible SOLO cuando:
 *  - tipoCita === CAMPO (17)
 *  - El contrato del beneficiario NO está en la lista de contratos exentos
 *
 * Dos modos según dsclasificacion del servicio:
 *  - "CAPACITACI…" → Capacitación: campos texto reducidos + anexos obligatorios
 *  - Otro valor     → Asesoría: campos texto + lista de asistentes + anexos opcionales
 *
 * HU1132419 — Legacy: CreateOrUpdateCumplimientoViewImpl.java activa el panel
 * para CAMPO no exento; CumplimientoFacade.java trata toda no capacitación
 * como acta de asesoría.
 */
@Component({
  selector: 'app-seccion-acta',
  standalone: true,
  imports: [
    ContadorCaracteresDirective,
    DropZoneDirective,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FieldsetModule,
    RadioButtonModule,
    InputTextModule,
    InputTextarea,
    ButtonModule,
    MessageModule,
    TooltipModule,
    BloqueAsistentesComponent
  ],
  template: `
    @if (mostrarPanel()) {
      <p-fieldset
        legend="Asociación de Acta de Visita"
        [toggleable]="true"
        styleClass="mb-4"
      >
        <!-- Tipo acta badge -->
        @if (tipoActa()) {
          <div class="mb-3">
            <span
              class="inline-block text-xs font-semibold px-2 py-1 rounded"
              [class]="
                tipoActa() === 'ASESOR'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-green-100 text-green-700'
              "
            >
              {{ tipoActa() === 'ASESOR' ? 'Asesoría' : 'Capacitación' }}
            </span>
          </div>
        }

        <!-- Descripción del servicio prestado -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Descripción del Servicio Prestado
            <span class="text-red-500">*</span>
          </label>
          <textarea
            pInputTextarea
            [(ngModel)]="descripcionServicioPrestado"
            (change)="
              descripcionServicioPrestado = sanitizeTextarea(descripcionServicioPrestado); emitir()
            "
            rows="3"
            [appContadorCaracteres]="maxTextoActa"
            placeholder="Describa el servicio prestado..."
            class="w-full"
            (ngModelChange)="emitir()"
          >
          </textarea>
        </div>

        <!-- Compromisos (solo ASESORÍA) -->
        @if (tipoActa() === 'ASESOR') {
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Compromisos Empresa Afiliada
            </label>
            <textarea
              pInputTextarea
              [(ngModel)]="compromisosEmpresaAfiliada"
              (change)="
                compromisosEmpresaAfiliada = sanitizeTextarea(compromisosEmpresaAfiliada); emitir()
              "
              rows="2"
              [appContadorCaracteres]="maxTextoActa"
              placeholder="Compromisos adquiridos por la empresa afiliada..."
              class="w-full"
              (ngModelChange)="emitir()"
            >
            </textarea>
          </div>

          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Compromisos SURA
            </label>
            <textarea
              pInputTextarea
              [(ngModel)]="compromisosSura"
              (change)="compromisosSura = sanitizeTextarea(compromisosSura); emitir()"
              rows="2"
              [appContadorCaracteres]="maxTextoActa"
              placeholder="Compromisos adquiridos por SURA..."
              class="w-full"
              (ngModelChange)="emitir()"
            >
            </textarea>
          </div>
        }

        <!-- Relación documentación (ASESORÍA) -->
        @if (tipoActa() === 'ASESOR') {
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Relación de Documentación
              @if (anexos().length > 0) {
                <span class="text-red-500">*</span>
              }
            </label>
            <textarea
              pInputTextarea
              [ngModel]="relacionDocumentacion()"
              (ngModelChange)="onRelacionDocumentacionChange($event)"
              (change)="onRelacionDocumentacionChange(sanitizeTextarea(relacionDocumentacion()))"
              rows="2"
              [appContadorCaracteres]="maxTextoActa"
              placeholder="Describa los documentos relacionados..."
              class="w-full"
            >
            </textarea>
          </div>
        }

        <!-- Lista de asistentes (solo ASESORÍA) — requerido al menos 1 -->
        @if (tipoActa() === 'ASESOR') {
          <div class="mb-4">
            <div class="flex items-center gap-1 mb-1">
              <span class="text-sm font-medium text-gray-700"
                >Asistentes a la Visita</span
              >
              <span class="text-red-500">*</span>
            </div>
            @if (asistentes().length === 0) {
              <p class="text-xs text-red-500 mb-2">
                Debe agregar al menos un asistente.
              </p>
            }
            <app-bloque-asistentes
              (asistentesChange)="onAsistentesChange($event)"
            >
            </app-bloque-asistentes>
          </div>
        }

        <!-- Envío notificación -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            ¿Enviar notificación al cliente?
          </label>
          <div class="flex flex-wrap gap-4 sm:gap-6">
            <div class="flex items-center gap-2">
              <p-radioButton
                name="envioNotificacion"
                value="S"
                [(ngModel)]="envioNotificacion"
                inputId="envioSi"
                (ngModelChange)="emitir()"
              >
              </p-radioButton>
              <label for="envioSi" class="cursor-pointer text-sm">Sí</label>
            </div>
            <div class="flex items-center gap-2">
              <p-radioButton
                name="envioNotificacion"
                value="N"
                [(ngModel)]="envioNotificacion"
                inputId="envioNo"
                (ngModelChange)="emitir()"
              >
              </p-radioButton>
              <label for="envioNo" class="cursor-pointer text-sm">No</label>
            </div>
          </div>
        </div>

        <!-- Carga de anexos -->
        <div class="mb-2">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Anexos
            @if (anexosRequeridos()) {
              <span class="text-red-500">*</span>
            }
            <span class="text-gray-400 text-xs ml-1"
              >(Máx. {{ maxAnexos }} archivos, 9 MB total)</span
            >
          </label>

          @if (anexosRequeridos() && anexos().length === 0) {
            <p class="text-xs text-red-500 mb-1">
              Debe adjuntar al menos un archivo.
            </p>
          }

          <!-- Botón de carga manual -->
          @if (anexos().length < maxAnexos) {
            <input
              #anexoInput
              type="file"
              class="hidden"
              [accept]="mimeAceptados"
              (change)="onFileSelected($event)"
            />
            <div
              appDropZone
              (archivoSoltado)="procesarArchivo($event)"
              class="flex flex-wrap items-center gap-2 mt-1 p-2 w-full border border-dashed border-gray-300 dark:border-gray-600 rounded-lg transition-colors"
            >
              <button
                pButton
                type="button"
                label="Seleccionar archivo"
                icon="pi pi-upload"
                class="p-button-outlined min-h-[44px]"
                (click)="anexoInput.click()"
              ></button>
              <span class="text-sm text-gray-500 dark:text-gray-400">
                o arrastre el archivo aquí
              </span>
            </div>
          }

          @if (errorAnexo()) {
            <p class="text-red-500 text-xs mt-1">{{ errorAnexo() }}</p>
          }

          @if (anexos().length > 0) {
            <ul class="mt-2 divide-y divide-gray-100 border rounded">
              @for (anexo of anexos(); track $index) {
                <li
                  class="flex items-center justify-between px-3 py-1.5 text-sm"
                >
                  <span
                    class="truncate min-w-0 flex-1 pr-2"
                    [title]="anexo.nombre"
                    >{{ anexo.nombre }}</span
                  >
                  <p-button
                    icon="pi pi-trash"
                    severity="danger"
                    [text]="true"
                    size="small"
                    styleClass="min-w-[44px] min-h-[44px]"
                    (onClick)="eliminarAnexo($index)"
                  >
                  </p-button>
                </li>
              }
            </ul>
          }
        </div>
      </p-fieldset>
    }
  `
})
export class SeccionActaComponent implements OnChanges {
  readonly maxTextoActa = MAX_TEXTO_ACTA;
  readonly sanitizeTextarea = sanitizeTextarea;
  // ── Inputs ──────────────────────────────────────────────────────────────────
  tipoCita = input<number>(0);
  dsclasificacion = input<string>('');
  contratoBeneficiario = input<string>('');
  contratosExentos = input<string>('');

  // ── Output ──────────────────────────────────────────────────────────────────
  datosActaChange = output<DatosActaFormValue | null>();

  // ── Estado interno ──────────────────────────────────────────────────────────
  descripcionServicioPrestado = '';
  compromisosEmpresaAfiliada = '';
  compromisosSura = '';
  envioNotificacion: 'S' | 'N' = 'N';

  /**
   * Texto de "Relación de Documentación" expuesto como signal porque el
   * cálculo de `anexosRequeridos` (Asesoría) depende de su contenido.
   */
  readonly relacionDocumentacion = signal<string>('');
  readonly asistentes = signal<Asistente[]>([]);
  readonly anexos = signal<AnexoActaFormValue[]>([]);
  readonly errorAnexo = signal<string | null>(null);

  readonly maxAnexos = MAX_ANEXOS;
  // Skill p8-filenet: incluir ppt, pptx, csv (misma whitelist que GenerarActaRestAdapter)
  readonly mimeAceptados =
    '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv';

  // ── Computados ───────────────────────────────────────────────────────────────

  readonly mostrarPanel = computed(() => {
    if (this.tipoCita() !== TIPO_CITA.CAMPO) return false;
    if (!this.tipoActa()) return false;
    // Legacy: isContratoExentoActa() compara con trim + equalsIgnoreCase
    const contrato = (this.contratoBeneficiario() ?? '').trim().toUpperCase();
    const exentos = (this.contratosExentos() ?? '')
      .split(',')
      .map((c) => c.trim().toUpperCase())
      .filter((c) => c.length > 0);
    return !exentos.includes(contrato);
  });

  readonly tipoActa = computed<TipoActa>(() => {
    const ds = (this.dsclasificacion() ?? '').trim().toUpperCase();
    if (!ds) return null;
    if (ds.startsWith('CAPACITACI')) return 'CAPACITACI';
    return 'ASESOR';
  });

  /**
   * Indica si los anexos del acta son obligatorios.
   * - Capacitación: siempre obligatorios (legacy CreateOrUpdateCumplimientoViewImpl.validarCamposActa).
   * - Asesoría: obligatorios SOLO si "Relación de Documentación" tiene contenido
   *   (regla bidireccional: anexos ↔ relación se exigen mutuamente).
   */
  readonly anexosRequeridos = computed<boolean>(() => {
    const tipo = this.tipoActa();
    if (tipo === 'CAPACITACI') return true;
    if (tipo === 'ASESOR')
      return (this.relacionDocumentacion() ?? '').trim().length > 0;
    return false;
  });

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    // Si el panel deja de mostrarse, emitir null para limpiar
    if (!this.mostrarPanel()) {
      this.datosActaChange.emit(null);
      return;
    }

    // Legacy: CreateOrUpdateCumplimientoViewImpl.configurarCamposActaCapacitacion()
    // Para capacitación no aplican compromisos, relación de documentación ni lista de asistentes.
    if (this.tipoActa() === 'CAPACITACI') {
      this.compromisosEmpresaAfiliada = '';
      this.compromisosSura = '';
      this.relacionDocumentacion.set('');
      this.asistentes.set([]);
    }

    // Emitir estado inicial al abrir/cambiar inputs para mantener sincronizado el padre.
    this.emitir();
  }

  // ── Handlers ─────────────────────────────────────────────────────────────────

  onAsistentesChange(lista: Asistente[]): void {
    this.asistentes.set(lista);
    this.emitir();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    // Reset input for re-selection of same file
    input.value = '';
    this.procesarArchivo(file);
  }

  /** Valida y agrega el anexo. Lo usan el clic y el arrastre. */
  procesarArchivo(file: File): void {
    this.errorAnexo.set(null);

    if (!MIME_PERMITIDOS.has(file.type)) {
      this.errorAnexo.set(
        'Tipo de archivo no permitido. Use PDF, JPG, PNG, DOC, DOCX, XLS, XLSX, PPT, PPTX o CSV.'
      );
      return;
    }

    if (this.anexos().length >= MAX_ANEXOS) {
      this.errorAnexo.set(`Solo se permiten hasta ${MAX_ANEXOS} archivos.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64Full = reader.result as string;
      // base64Full = "data:<mime>;base64,<data>"
      const base64Data = base64Full.split(',')[1] ?? '';

      // Validar tamaño acumulado
      const totalBytes =
        this.anexos().reduce((sum, a) => sum + a.contenidoBase64.length, 0) +
        base64Data.length;

      if (totalBytes > MAX_BASE64_BYTES) {
        this.errorAnexo.set(
          'El tamaño total de los archivos supera los 9 MB permitidos.'
        );
        return;
      }

      this.anexos.update((list) => [
        ...list,
        {
          nombre: file.name,
          contentType: file.type,
          contenidoBase64: base64Data
        }
      ]);
      this.emitir();
    };
    reader.readAsDataURL(file);
  }

  eliminarAnexo(index: number): void {
    this.anexos.update((list) => list.filter((_, i) => i !== index));
    this.errorAnexo.set(null);
    this.emitir();
  }

  onRelacionDocumentacionChange(value: string): void {
    this.relacionDocumentacion.set(value ?? '');
    this.emitir();
  }

  emitir(): void {
    if (!this.mostrarPanel()) {
      this.datosActaChange.emit(null);
      return;
    }
    const value: DatosActaFormValue = {
      descripcionServicioPrestado: this.descripcionServicioPrestado,
      compromisosEmpresaAfiliada: this.compromisosEmpresaAfiliada,
      compromisosSura: this.compromisosSura,
      relacionDocumentacion: this.relacionDocumentacion(),
      envioNotificacion: this.envioNotificacion,
      tipoActa: this.tipoActa(),
      asistentes: this.asistentes(),
      anexos: this.anexos()
    };
    this.datosActaChange.emit(value);
  }
}
